import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Settings, Shield, Banknote, Wrench, ChevronRight, FileText, ClipboardCheck, FileCheck, Radio, Ban, RotateCcw, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface ManageContractSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onOpenSetup: () => void;
    onOpenInsurance: () => void;
    onOpenLoan: () => void;
    onOpenMaintenance: () => void;
    /** Opens the contract's inspection page; omitted for an admin without the inspections key. */
    onOpenInspection?: () => void;
    onOpenRoadworthiness: () => void;
    onManageTrackers: () => void;
    currentStatus?: string | null;
    onMarkComplete?: () => void;
    onUndoComplete?: () => void;
    onTerminate?: () => void;
    onUndoTerminate?: () => void;
}

interface ActionCardProps {
    icon: any;
    title: string;
    description: string;
    onClick: () => void;
    colorClass: string;
    disabled?: boolean;
}

const ActionCard = ({ icon: Icon, title, description, onClick, colorClass, disabled }: ActionCardProps) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
            "w-full text-left group relative transition-all duration-300 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            disabled ? "opacity-60 cursor-not-allowed" : "hover:scale-[1.01]"
        )}
    >
        <Card className={cn(
            "h-full border border-border shadow-sm transition-all duration-300",
            !disabled && "group-hover:shadow-md group-hover:border-primary/20"
        )}>
            <div className={cn(
                "absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500",
                colorClass.replace('text-', 'bg-')
            )} />

            <CardContent className="p-3 flex items-center gap-3">
                <div className={cn(
                    "p-2 rounded-lg transition-all duration-300 shadow-sm shrink-0",
                    "bg-white dark:bg-muted border border-muted",
                    !disabled && "group-hover:scale-105",
                    colorClass
                )}>
                    <Icon className="h-5 w-5" />
                </div>

                <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-foreground tracking-tight flex items-center gap-2">
                            {title}
                            {disabled && (
                                <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                                    Soon
                                </Badge>
                            )}
                        </h3>
                        {!disabled && (
                            <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug truncate">
                        {description}
                    </p>
                </div>
            </CardContent>
        </Card>
    </button>
);

export default function ManageContractSheet({
    open,
    onOpenChange,
    onOpenSetup,
    onOpenInsurance,
    onOpenLoan,
    onOpenMaintenance,
    onOpenInspection,
    onOpenRoadworthiness,
    onManageTrackers,
    currentStatus,
    onMarkComplete,
    onUndoComplete,
    onTerminate,
    onUndoTerminate,
}: ManageContractSheetProps) {
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 !h-auto !w-[90vw] sm:!w-[500px] !max-w-none rounded-2xl border shadow-2xl bg-background p-6 flex flex-col gap-6"
            >
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-primary font-medium tracking-wide text-xs uppercase">
                        <Settings className="h-3.5 w-3.5" />
                        Management
                    </div>
                    <SheetTitle className="text-2xl font-bold">Manage Contract</SheetTitle>
                    <p className="text-muted-foreground text-sm">
                        Select an action to perform on this contract.
                    </p>
                </div>

                <div className="grid gap-2 overflow-y-auto flex-1 min-h-0 p-1 -m-1">
                    <ActionCard
                        icon={FileText}
                        title="Contract Setup"
                        description="View checklist, vehicle details, and driver info."
                        onClick={() => {
                            onOpenChange(false);
                            onOpenSetup();
                        }}
                        colorClass="text-blue-600"
                    />

                    <ActionCard
                        icon={Shield}
                        title="Add Insurance"
                        description="Create a policy, link payments, and upload docs."
                        onClick={() => {
                            onOpenChange(false);
                            onOpenInsurance();
                        }}
                        colorClass="text-indigo-600"
                    />

                    <ActionCard
                        icon={Banknote}
                        title="Add Advance"
                        description="Issue an advance with custom repayment terms."
                        onClick={() => {
                            onOpenChange(false);
                            onOpenLoan();
                        }}
                        colorClass="text-emerald-600"
                    />

                    <ActionCard
                        icon={Wrench}
                        title="Add Maintenance"
                        description="Record maintenance, repairs, and services."
                        onClick={() => {
                            onOpenChange(false);
                            onOpenMaintenance();
                        }}
                        colorClass="text-orange-600"
                    />

                    {onOpenInspection && (
                        <ActionCard
                            icon={ClipboardCheck}
                            title="Inspections"
                            description="Inspect or re-check the car; this round and its history."
                            onClick={() => {
                                onOpenChange(false);
                                onOpenInspection();
                            }}
                            colorClass="text-cyan-600"
                        />
                    )}

                    <ActionCard
                        icon={FileCheck}
                        title="Add Roadworthiness"
                        description="Upload roadworthiness certificate."
                        onClick={() => {
                            onOpenChange(false);
                            onOpenRoadworthiness();
                        }}
                        colorClass="text-purple-600"
                    />

                    <ActionCard
                        icon={Radio}
                        title="Manage Trackers"
                        description="Assign or change vehicle trackers."
                        onClick={() => {
                            onOpenChange(false);
                            onManageTrackers();
                        }}
                        colorClass="text-rose-600"
                    />

                    {/* Mark Complete / Terminate / Undo Terminate */}
                    {currentStatus === 'IN_PROGRESS' && (onMarkComplete || onTerminate) && (
                        <div className="border-t border-border my-1" />
                    )}

                    {currentStatus === 'IN_PROGRESS' && onMarkComplete && (
                        <ActionCard
                            icon={CheckCircle2}
                            title="Mark Complete"
                            description="Mark this contract as completed."
                            onClick={() => {
                                onOpenChange(false);
                                onMarkComplete();
                            }}
                            colorClass="text-green-600"
                        />
                    )}

                    {currentStatus === 'IN_PROGRESS' && onTerminate && (
                        <ActionCard
                            icon={Ban}
                            title="Terminate Contract"
                            description="End this contract and mark it as terminated."
                            onClick={() => {
                                onOpenChange(false);
                                onTerminate();
                            }}
                            colorClass="text-red-600"
                        />
                    )}

                    {currentStatus === 'COMPLETED' && onUndoComplete && (
                        <>
                            <div className="border-t border-border my-1" />
                            <ActionCard
                                icon={RotateCcw}
                                title="Undo Completion"
                                description="Reactivate this contract back to active status."
                                onClick={() => {
                                    onOpenChange(false);
                                    onUndoComplete();
                                }}
                                colorClass="text-amber-600"
                            />
                        </>
                    )}

                    {currentStatus === 'TERMINATED' && onUndoTerminate && (
                        <>
                            <div className="border-t border-border my-1" />
                            <ActionCard
                                icon={RotateCcw}
                                title="Undo Termination"
                                description="Reactivate this contract back to active status."
                                onClick={() => {
                                    onOpenChange(false);
                                    onUndoTerminate();
                                }}
                                colorClass="text-amber-600"
                            />
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
